import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * ReportAuthContext
 *
 * Stores:
 *  - reportToken   : JWT issued by the backend after successful payment
 *  - orderId       : UUID of the completed order
 *  - searchData    : { plate, state, searchType } from the home page search
 *  - selectedReport: { reportType, price, name } chosen on the report-types page
 *  - customerInfo  : { email, firstName, lastName } from the checkout form
 *
 * The reportToken is persisted to sessionStorage so a page refresh on /report
 * still works within the same browser tab/session.
 */

const SESSION_KEY = 'ppsr_report_token';
const ORDER_KEY   = 'ppsr_order_id';

const ReportAuthContext = createContext(null);

export function ReportAuthProvider({ children }) {
  const [reportToken, setReportToken]     = useState(() => sessionStorage.getItem(SESSION_KEY) || null);
  const [orderId, setOrderId]             = useState(() => sessionStorage.getItem(ORDER_KEY) || null);
  const [searchData, setSearchData]       = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [customerInfo, setCustomerInfo]   = useState(null);

  const saveReportToken = useCallback((token, id) => {
    sessionStorage.setItem(SESSION_KEY, token);
    sessionStorage.setItem(ORDER_KEY, id);
    setReportToken(token);
    setOrderId(id);
  }, []);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ORDER_KEY);
    setReportToken(null);
    setOrderId(null);
    setSearchData(null);
    setSelectedReport(null);
    setCustomerInfo(null);
  }, []);

  const hasValidToken = Boolean(reportToken);

  return (
    <ReportAuthContext.Provider
      value={{
        reportToken,
        orderId,
        searchData,
        setSearchData,
        selectedReport,
        setSelectedReport,
        customerInfo,
        setCustomerInfo,
        saveReportToken,
        clearSession,
        hasValidToken,
      }}
    >
      {children}
    </ReportAuthContext.Provider>
  );
}

export function useReportAuth() {
  const ctx = useContext(ReportAuthContext);
  if (!ctx) throw new Error('useReportAuth must be used within ReportAuthProvider');
  return ctx;
}
